import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Per-tab flag marking that we already kicked off an interactive sign-in redirect. A plain ref is
 * not enough because MSAL's redirect flow reloads the page (resetting refs); persisting in
 * sessionStorage ensures auto-login fires at most once per tab and can never spin into a
 * redirect-to-Microsoft loop if the returning session isn't recognized immediately.
 */
const AUTO_LOGIN_FLAG = 'bo.adminLogin.autoAttempted';

export function AdminLoginPage(): React.ReactElement {
  useDocumentTitle('Sign in · Triplicity Backoffice');
  const { authReady, isAuthenticated, login, azureAuthConfigured } = useAuth();

  const stubConfigured = process.env.REACT_APP_AUTH_STUB === 'true';
  const authConfigured = azureAuthConfigured || stubConfigured;

  useEffect(() => {
    if (isAuthenticated) {
      // Reset so a later explicit sign-out/sign-in can auto-trigger again.
      try {
        sessionStorage.removeItem(AUTO_LOGIN_FLAG);
      } catch {
        /* sessionStorage unavailable */
      }
      return;
    }
    // Don't auto-trigger an interactive redirect off a transient MSAL startup blip — wait until
    // hydration confirms this is a real "signed out" state, so we never race MSAL's own in-flight
    // handleRedirectPromise() with a second loginRedirect().
    if (!authReady) {
      return;
    }
    if (!authConfigured) {
      return;
    }
    let alreadyAttempted = false;
    try {
      alreadyAttempted = sessionStorage.getItem(AUTO_LOGIN_FLAG) === '1';
      if (!alreadyAttempted) {
        sessionStorage.setItem(AUTO_LOGIN_FLAG, '1');
      }
    } catch {
      /* sessionStorage unavailable: fall through and attempt once */
    }
    if (alreadyAttempted) {
      return;
    }
    login();
  }, [authReady, isAuthenticated, login, authConfigured]);

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (!authConfigured) {
    return (
      <div>
        <PageHeader title="Admin sign-in" />
        <EmptyState
          title="Authentication is not configured"
          description="Add REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID to your environment, or set REACT_APP_AUTH_STUB=true for offline development."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Admin sign-in" subtitle="Redirecting you to sign in…" />
      <div className="bo-card">
        <Spinner label="Signing you in…" />
        <p className="bo-muted" style={{ marginTop: '1rem' }}>
          If you are not redirected automatically, use the button below.
        </p>
        <button type="button" className="bo-btn bo-btn-primary" onClick={login}>
          {azureAuthConfigured ? 'Sign in' : 'Continue (stub sign-in)'}
        </button>
      </div>
    </div>
  );
}
